import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { FinnairOrder, FinnairTravelEndpoint } from '@fcom/dapi/api/models';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext, ThirdPartyAncillariesProvider } from '@fcom/common';
import { ConfigService } from '@fcom/core/index';

import { ManageBookingService } from '../../../../services';
import { ThirdPartyAncillariesAttributes, ThirdPartyDeepLink } from '../../../../interfaces';
import { mapToGaState, mapToGaString } from '../../../../constants';

@Component({
  selector: 'fin-mmb-third-party-links',
  templateUrl: './third-party-links.component.html',
  styleUrls: ['./third-party-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ThirdPartyLinksComponent implements OnInit, OnChanges, AfterViewInit {
  private thirdPartyAncillaries: ThirdPartyAncillariesAttributes[] = [
    {
      partner: ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL,
      cmsKey: 'carTrawlerRental',
      partnerIcon: SvgLibraryIcon.RENTAL_CAR,
      affiliateId: 'finnair',
      campaignId: 'manage-booking',
      clientId: '398065',
      defaultStay: 7,
    },
    {
      partner: ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER,
      cmsKey: 'carTrawlerTransfer',
      partnerIcon: SvgLibraryIcon.RIDE_SHARE,
      affiliateId: 'finnair',
      campaignId: 'manage-booking',
      clientId: '396807',
      defaultStay: 7,
    },
    {
      partner: ThirdPartyAncillariesProvider.EXPEDIA_HOTEL,
      cmsKey: 'expediaHotel',
      partnerIcon: SvgLibraryIcon.HOTEL,
    },
  ];

  @Input()
  order: FinnairOrder;

  @Input()
  arriving: FinnairTravelEndpoint;

  @Input()
  leaving: FinnairTravelEndpoint | undefined;

  @Input()
  partnerEligibilities: Partial<Record<ThirdPartyAncillariesProvider, boolean>> = {};

  deeplinks: ThirdPartyDeepLink[] = [];
  destinationCity: string;

  constructor(
    private manageBookingService: ManageBookingService,
    private gtmService: GtmService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.refreshDeepLinks();
  }

  ngOnChanges(): void {
    this.refreshDeepLinks();
  }

  ngAfterViewInit(): void {
    this.deeplinks.forEach((deeplink) => {
      this.gtmService.trackElement(
        mapToGaString[deeplink.partner],
        GaContext.THIRD_PARTY_ANCILLARY,
        ElementTypes.LINK,
        mapToGaState[deeplink.partner],
        ElementActions.VIEW
      );
    });
  }

  trackClick(link: ThirdPartyDeepLink): void {
    this.gtmService.trackElement(
      mapToGaString[link.partner],
      GaContext.THIRD_PARTY_ANCILLARY,
      ElementTypes.LINK,
      mapToGaState[link.partner],
      ElementActions.CLICK
    );
  }

  private refreshDeepLinks(): void {
    this.deeplinks = this.manageBookingService
      .getThirdPartyDeepLinks(this.order, this.arriving, this.leaving, this.thirdPartyAncillaries)
      .filter(
        (thirdParty) =>
          (this.partnerEligibilities[thirdParty.partner] ?? true) &&
          [
            ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL,
            this.configService.cfg.enableMMBHotelDeeplink
              ? ThirdPartyAncillariesProvider.EXPEDIA_HOTEL
              : ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER,
          ].includes(thirdParty.partner)
      );

    this.destinationCity = this.order.locations[this.arriving.locationCode].cityName;
  }
}
